/**
 * Environment variable helper for Alpaca API
 * Handles both Vite and non-Vite environments
 * Supports both paper and live trading environments
 */

import { checkAlpacaCredentials } from './debug';

// Define known good API keys
// Note: In production, these should be loaded from environment variables
// Updated with correct Alpaca API credentials
const PAPER_KEY = 'PK3A6D7UMTWJ8MZZ1G4U';
const PAPER_SECRET = 'Ci7I3g0lfFRb9yI1EZJguiymxWAJfNjEYYMLzCVx';

// Live trading keys (correct credentials)
const LIVE_KEY = 'AK8DG0QZWT7DACDHV927';
const LIVE_SECRET = 'EPAjsSQJbBTyNvvLdnGGZ0m8rM4za1wX7sR5TSzk';

export const getAlpacaEnv = () => {
  // IMPORTANT: Paper trading keys are returning 403 errors for data access
  // We need to use live API keys for market data but keep paper mode for trading
  // This is a common setup - live keys for data, paper mode for trading
  
  // Get the appropriate API keys based on environment
  let keyId = '';
  let secretKey = '';
  
  // For market data access, we'll use live API keys as they have proper permissions
  // Trading operations will still use paper mode via the baseUrl
  const useDataKeys = true; // Always use working data keys
  const usePaperTrading = process.env.VITE_ALPACA_PAPER === 'false' ? false : true; // Default to paper trading for safety
  
  if (useDataKeys) {
    // Use live API keys for data access (market data, news, etc.)
    keyId = process.env.VITE_ALPACA_LIVE_API_KEY_ID || 
            process.env.ALPACA_LIVE_API_KEY || 
            process.env.VITE_ALPACA_API_KEY_ID || 
            process.env.ALPACA_KEY_ID || 
            LIVE_KEY; // Correct working live key for data access
            
    secretKey = process.env.VITE_ALPACA_LIVE_SECRET_KEY || 
                process.env.ALPACA_LIVE_SECRET || 
                process.env.VITE_ALPACA_SECRET_KEY || 
                process.env.ALPACA_SECRET || 
                LIVE_SECRET; // Correct working live secret for data access
    
    console.log('Using live API keys for data access (market data, news) - this is safe for read-only operations');
  } else {
    // Fallback to paper keys (now with correct credentials)
    keyId = process.env.VITE_ALPACA_PAPER_API_KEY_ID || 
            process.env.ALPACA_PAPER_API_KEY || 
            process.env.ALPACA_KEY_ID || 
            PAPER_KEY;
            
    secretKey = process.env.VITE_ALPACA_PAPER_SECRET_KEY || 
                process.env.ALPACA_PAPER_SECRET || 
                process.env.ALPACA_SECRET || 
                PAPER_SECRET;
    
    console.log('Using paper trading API keys - may experience 403 errors for data access');
  }
  
  // For backward compatibility in case code still references these
  if (typeof window !== 'undefined') {
    // We're in a browser environment
    (window as any).ALPACA_KEY_ID = keyId;
    (window as any).ALPACA_SECRET = secretKey;
    (window as any).ALPACA_PAPER = usePaperTrading;
  }
  
  // Register debug utility in window (browser only)
  if (typeof window !== 'undefined') {
    checkAlpacaCredentials();
  }
  
  // Log the environment being used (but not the full credentials)
  console.log(`Alpaca API initialized: Using ${useDataKeys ? 'LIVE' : 'PAPER'} keys for data access`);
  console.log(`Trading mode: ${usePaperTrading ? 'PAPER' : 'LIVE'} (for order placement)`);
  console.log(`API Key: ${keyId.substring(0, 4)}...${keyId.substring(keyId.length - 4)}`);
  
  return {
    keyId,
    secretKey,
    paper: usePaperTrading, // This controls trading mode, not data access
    // Use paper trading URLs for safety, but live keys for data access
    baseUrl: usePaperTrading ? 'https://paper-api.alpaca.markets' : 'https://api.alpaca.markets',
    dataBaseUrl: 'https://data.alpaca.markets' // Always use live data endpoint with live keys
  };
};